import { LeadCreateModal } from '@/components/leads/LeadCreateModal';
import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { SavedViewsBar } from '@/components/SavedViewsBar';
import { Card } from '@/components/Card';
import { Button } from '@/components/Button';
import { Pagination } from '@/components/Pagination';
import { Input } from '@/components/Input';
import { leadsService, Lead, LeadStatus, LeadSource, ListLeadsResponse } from '@/services/leads.service';
import { departmentsService, Department } from '@/services/departments.service';
import { useAuth } from '@/hooks/useAuth';
import { useActiveAccount } from '@/hooks/useActiveAccount';
import { leadsAnalyticsService } from '@/services/leads-analytics.service';
import { LeadStatusPill } from '@/components/leads/LeadStatusPill';
import {
  Plus, Search, Clock, LayoutList, Columns, GripVertical, Download, BarChart2, Trash2, Upload, AlarmClock,
} from 'lucide-react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { LoadErrorState } from '@/components/ListStates';
import { LeadImportModal } from '@/components/leads/LeadImportModal';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { formatDistanceToNow } from 'date-fns';
import emptyLeadsIllustration from '@/assets/illustrations/empty-leads.svg';
import { useLabels, useCompanyCurrency } from '@/contexts/WorkspaceContext';
import { Select } from '@/components/Select';

// ─── localStorage helpers ─────────────────────────────────────────────────────

const VIEW_MODE_KEY = 'medprep_leads_view_mode';
function getStoredViewMode(): 'list' | 'kanban' {
  try {
    const v = localStorage.getItem(VIEW_MODE_KEY);
    return v === 'kanban' ? 'kanban' : 'list';
  } catch {
    return 'list';
  }
}
function setStoredViewMode(m: 'list' | 'kanban') {
  try { localStorage.setItem(VIEW_MODE_KEY, m); } catch { /* noop */ }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function initialsFor(name?: string, phone?: string): string {
  if (name && name.trim()) {
    return name.trim().split(' ').slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('');
  }
  return phone?.slice(-2) ?? '??';
}

function relativeTime(ts: string) {
  try { return formatDistanceToNow(new Date(ts), { addSuffix: true }); }
  catch { return ts; }
}

/** Red-clock condition: follow-up date passed and the lead is still open. */
function isFollowUpOverdue(lead: Lead): boolean {
  if (!lead.nextFollowUpAt) return false;
  if (!['new', 'contacted', 'qualified'].includes(lead.status)) return false;
  return new Date(lead.nextFollowUpAt).getTime() < Date.now();
}

const SOURCE_LABELS: Record<string, string> = {
  ctwa:             '📘 Meta ad',
  lead_ad_form:     '📋 Meta lead form',
  tiktok_lead_ad:   '🎵 TikTok',
  google_lead_form: '🔎 Google form',
  google_ads:       '🔎 Google ad',
  web_form:         '🌐 Web form',
  web_chat:         '💬 Website chat',
  chat_api:         '🔌 App chat',
  email:            '✉️ Email',
  inbound_keyword:  '💬 WhatsApp',
  manual:           '✋ Manual',
};

function provenanceLabel(lead: Lead): string | null {
  // CTWA: prefer the source URL hint to disambiguate Facebook vs Instagram
  if (lead.source === 'ctwa') {
    const url = (lead.referralSourceUrl ?? '').toLowerCase();
    if (url.includes('instagram.com')) return '📷 Instagram ad';
    if (url.includes('facebook.com') || url.includes('fb.com')) return '📘 Facebook ad';
  }
  const base = SOURCE_LABELS[lead.source] ?? null;
  if (base && lead.referralHeadline) return `${base} · ${lead.referralHeadline}`;
  if (base && lead.sourceCampaignId) return `${base} · camp ${lead.sourceCampaignId}`;
  return base;
}

// ─── Status filter tabs ───────────────────────────────────────────────────────

const STATUS_TABS: { label: string; value: string }[] = [
  { label: 'All', value: '' },
  { label: 'New', value: 'new' },
  { label: 'Contacted', value: 'contacted' },
  { label: 'Qualified', value: 'qualified' },
  { label: 'Won', value: 'won' },
  { label: 'Lost', value: 'lost' },
];

const KANBAN_COLUMNS: { status: LeadStatus; label: string; color: string; headerColor: string }[] = [
  { status: 'new',       label: 'New',       color: 'bg-info',    headerColor: 'bg-info-soft border-info/20 text-info' },
  { status: 'contacted', label: 'Contacted', color: 'bg-warning', headerColor: 'bg-warning-soft border-warning/20 text-warning' },
  { status: 'qualified', label: 'Qualified', color: 'bg-primary', headerColor: 'bg-primary-soft border-primary/20 text-primary' },
  { status: 'won',       label: 'Won',       color: 'bg-success', headerColor: 'bg-success-soft border-success/20 text-success' },
  { status: 'lost',      label: 'Lost',      color: 'bg-danger',  headerColor: 'bg-danger-soft border-danger/20 text-danger' },
];

// ─── Avatar ───────────────────────────────────────────────────────────────────

function LeadAvatar({ name, phone, size = 8 }: { name?: string; phone?: string; size?: number }) {
  const initials = initialsFor(name, phone);
  return (
    <div
      className={clsx(
        'flex shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary font-bold',
        size === 8 && 'h-8 w-8 text-xs',
        size === 6 && 'h-6 w-6 text-[9px]',
      )}
    >
      {initials}
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyState({ filtered, onClear, onAdd }: { filtered: boolean; onClear: () => void; onAdd: () => void }) {
  const t = useLabels();
  return (
    <div className="flex flex-col items-center gap-4 px-5 py-12 text-center">
      <img src={emptyLeadsIllustration} alt="" aria-hidden width={100} height={100} className="opacity-90 select-none pointer-events-none" />
      <div>
        <p className="text-sm font-semibold text-text-primary">
          {filtered ? `No ${t('leads').toLowerCase()} match your filters` : 'Your inbox is empty'}
        </p>
        <p className="mt-1 text-xs text-text-muted max-w-xs">
          {filtered
            ? 'Try a different search, or clear the filters to see everything.'
            : `${t('leads')} will appear here when people message you on WhatsApp, reply to your ads or fill in your forms.`}
        </p>
      </div>
      {filtered ? (
        <Button variant="secondary" size="sm" onClick={onClear}>Clear filters</Button>
      ) : (
        <Button size="sm" iconLeft={<Plus size={14} />} onClick={onAdd}>Add {t('leads', 'singular').toLowerCase()}</Button>
      )}
    </div>
  );
}

// ─── Kanban Card ─────────────────────────────────────────────────────────────

function KanbanCard({
  lead,
  onDragStart,
}: {
  lead: Lead;
  onDragStart: (e: React.DragEvent<HTMLDivElement>, lead: Lead) => void;
}) {
  const companyCurrency = useCompanyCurrency();
  const navigate = useNavigate();
  const label = provenanceLabel(lead);
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, lead)}
      onClick={() => navigate(`/leads/${lead.leadUid}`)}
      className={clsx(
        'group cursor-pointer rounded-xl border border-border bg-surface p-3.5 shadow-soft transition-all',
        'hover:shadow-elevated hover:-translate-y-0.5 active:translate-y-0',
        'select-none',
      )}
    >
      <div className="flex items-start gap-2.5">
        <LeadAvatar name={lead.profileName} phone={lead.phone} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-1">
            <p className="truncate text-[12.5px] font-semibold text-text-primary leading-tight">
              {lead.profileName || lead.email || 'New lead'}
            </p>
            <GripVertical size={12} className="shrink-0 text-text-muted opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          <p className="mt-0.5 font-mono text-[11px] text-text-muted truncate">{lead.phone || lead.email || 'No contact details'}</p>
        </div>
      </div>

      {label && (
        <p className="mt-2 truncate rounded-full border border-border bg-surface-alt px-2 py-0.5 text-[10.5px] text-text-secondary font-medium">
          {label}
        </p>
      )}

      {lead.assignedToUserUid && (
        <div className="mt-2 flex items-center gap-1.5">
          <LeadAvatar phone={lead.assignedToUserUid} size={6} />
          <span className="text-[10px] text-text-muted truncate max-w-[120px]">{lead.assignedToUserUid}</span>
        </div>
      )}

      {/* Won deal value indicator */}
      {lead.status === 'won' && lead.estimatedValueAmount != null && (
        <div className="mt-2 flex items-center gap-1 rounded-full bg-success-soft border border-success/20 px-2 py-0.5 text-[11px] font-semibold text-success w-fit">
          {lead.estimatedValueCurrency ?? companyCurrency} {lead.estimatedValueAmount.toLocaleString()}
        </div>
      )}

      <div className="mt-2 flex items-center gap-1 text-[10px] text-text-muted">
        <Clock size={10} />
        {relativeTime(lead.createdAt)}
      </div>
    </div>
  );
}

// ─── Kanban Column ────────────────────────────────────────────────────────────

function KanbanColumn({
  col,
  leads,
  onDragStart,
  onDrop,
  onDragOver,
  dragOver,
}: {
  col: typeof KANBAN_COLUMNS[number];
  leads: Lead[];
  onDragStart: (e: React.DragEvent<HTMLDivElement>, lead: Lead) => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>, status: LeadStatus) => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>, status: LeadStatus) => void;
  dragOver: LeadStatus | null;
}) {
  const t = useLabels();
  return (
    <div
      className="flex min-w-[220px] flex-1 flex-col gap-0"
      onDrop={(e) => onDrop(e, col.status)}
      onDragOver={(e) => { e.preventDefault(); onDragOver(e, col.status); }}
      onDragLeave={() => onDragOver({ preventDefault: () => {} } as React.DragEvent<HTMLDivElement>, null as unknown as LeadStatus)}
    >
      {/* Column header */}
      <div
        className={clsx(
          'mb-3 flex items-center justify-between rounded-xl border px-3.5 py-2.5',
          col.headerColor,
        )}
      >
        <div className="flex items-center gap-2">
          <span className={clsx('h-2 w-2 rounded-full', col.color)} />
          <span className="text-xs font-bold tracking-wide uppercase">{col.label}</span>
        </div>
        <span className="rounded-full bg-surface/60 px-1.5 py-0.5 text-[11px] font-semibold">
          {leads.length}
        </span>
      </div>

      {/* Drop zone */}
      <div
        className={clsx(
          'flex-1 space-y-2.5 rounded-xl border-2 border-dashed p-2 transition-colors min-h-[160px]',
          dragOver === col.status
            ? 'border-primary bg-primary-soft/20'
            : 'border-transparent bg-transparent',
        )}
      >
        {leads.map((lead) => (
          <KanbanCard key={lead.leadUid} lead={lead} onDragStart={onDragStart} />
        ))}

        {leads.length === 0 && dragOver !== col.status && (
          <div className="flex h-20 items-center justify-center rounded-lg">
            <p className="text-[11px] text-text-muted italic">No {t('leads').toLowerCase()}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Kanban board ─────────────────────────────────────────────────────────────

function KanbanBoard({
  leads,
  setLeads,
}: {
  leads: Lead[];
  setLeads: React.Dispatch<React.SetStateAction<Lead[]>>;
}) {
  const [dragLeadUid, setDragLeadUid] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<LeadStatus | null>(null);

  // Only non-spam leads in kanban
  const visibleLeads = leads.filter((l) => l.status !== 'spam');

  const columnLeads = (status: LeadStatus) =>
    visibleLeads.filter((l) => l.status === status);

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, lead: Lead) => {
    setDragLeadUid(lead.leadUid);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>, targetStatus: LeadStatus) => {
    e.preventDefault();
    setDragOver(null);
    if (!dragLeadUid) return;

    const dragged = leads.find((l) => l.leadUid === dragLeadUid);
    if (!dragged || dragged.status === targetStatus) {
      setDragLeadUid(null);
      return;
    }

    // Optimistic update
    setLeads((prev) =>
      prev.map((l) => l.leadUid === dragLeadUid ? { ...l, status: targetStatus } : l),
    );
    setDragLeadUid(null);

    try {
      await leadsService.update(dragLeadUid, { status: targetStatus });
    } catch {
      // Rollback
      setLeads((prev) =>
        prev.map((l) => l.leadUid === dragLeadUid ? { ...l, status: dragged.status } : l),
      );
      toast.error('Failed to update lead status');
    }
  };

  const handleDragOver = (_e: React.DragEvent<HTMLDivElement>, status: LeadStatus) => {
    setDragOver(status);
  };

  return (
    <div className="flex gap-4 overflow-x-auto pb-4">
      {KANBAN_COLUMNS.map((col) => (
        <KanbanColumn
          key={col.status}
          col={col}
          leads={columnLeads(col.status)}
          onDragStart={handleDragStart}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          dragOver={dragOver}
        />
      ))}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function LeadsInbox() {
  const t = useLabels();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { active: activeAccount, loading: accountsLoading } = useActiveAccount();

  const [data, setData] = useState<ListLeadsResponse | null>(null);
  const [listError, setListError] = useState(false);
  const [allLeads, setAllLeads] = useState<Lead[]>([]);
  const [kanbanError, setKanbanError] = useState(false);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [source, setSource] = useState<'' | LeadSource>('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const [mineOnly, setMineOnly] = useState(false);
  const [needsAttention, setNeedsAttention] = useState(searchParams.get('needsAttention') === '1');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'kanban'>(getStoredViewMode);
  // Starts true so the board shows its skeleton, not the empty state, until the first load lands.
  const [kanbanLoading, setKanbanLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState<Lead | null>(null);
  const [deletingLead, setDeletingLead] = useState(false);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [departmentFilter, setDepartmentFilter] = useState<string>('');

  useEffect(() => {
    departmentsService.list().then(setDepartments).catch(() => setDepartments([]));
  }, []);

  const deptByUid = (uid?: string) =>
    uid ? departments.find((d) => d.deptUid === uid) : undefined;

  const confirmDelete = async () => {
    if (!deleteCandidate) return;
    setDeletingLead(true);
    try {
      await leadsService.remove(deleteCandidate.leadUid);
      toast.success(`${t('leads', 'singular')} deleted`);
      setData((prev) =>
        prev
          ? { ...prev, items: prev.items.filter((l) => l.leadUid !== deleteCandidate.leadUid), total: Math.max(0, prev.total - 1) }
          : prev,
      );
      setAllLeads((prev) => prev.filter((l) => l.leadUid !== deleteCandidate.leadUid));
      setDeleteCandidate(null);
    } catch {
      toast.error(`Couldn't delete the ${t('leads', 'singular').toLowerCase()}`);
    } finally {
      setDeletingLead(false);
    }
  };

  // Sync campaign filter from URL params (coming from analytics → "View leads")
  const campaignFilter = searchParams.get('sourceCampaignId') ?? undefined;

  const switchView = (mode: 'list' | 'kanban') => {
    setViewMode(mode);
    setStoredViewMode(mode);
  };

  // List data (paginated)
  const loadList = useCallback(() => {
    // The list endpoint treats a missing accountUid as "all accounts"
    // (leads.service.ts:93), so firing before the account picker resolves
    // briefly shows leads from every connected WhatsApp number.
    if (accountsLoading) return;
    setListError(false);
    leadsService
      .list({
        q: q || undefined,
        status: status || undefined,
        source: source || undefined,
        sourceCampaignId: campaignFilter,
        assignedToUserUid: mineOnly ? user?.userUid : undefined,
        assignedToDepartmentUid: departmentFilter || undefined,
        accountUid: activeAccount?.accountUid,
        needsAttention: needsAttention ? '1' : undefined,
        followUpBefore: overdueOnly ? new Date().toISOString() : undefined,
        from: fromDate || undefined,
        to: toDate || undefined,
        page,
        limit: 25,
      } as any)
      .then(setData)
      .catch(() => setListError(true));
  }, [q, status, source, page, fromDate, toDate, campaignFilter, mineOnly, needsAttention, overdueOnly, departmentFilter, user?.userUid, activeAccount?.accountUid, accountsLoading]);

  useEffect(() => {
    if (viewMode === 'list') loadList();
  }, [loadList, viewMode]);

  // Kanban data (all non-spam leads, up to 200)
  const loadKanban = useCallback(() => {
    if (viewMode !== 'kanban' || accountsLoading) return;
    setKanbanLoading(true);
    setKanbanError(false);
    leadsService
      .list({
        limit: 200,
        status: q ? undefined : (status || undefined),
        source: source || undefined,
        sourceCampaignId: campaignFilter,
        accountUid: activeAccount?.accountUid,
      } as any)
      .then((res) => setAllLeads(res.items))
      .catch(() => setKanbanError(true))
      .finally(() => setKanbanLoading(false));
  }, [viewMode, q, status, source, campaignFilter, activeAccount?.accountUid, accountsLoading]);

  useEffect(() => {
    loadKanban();
  }, [loadKanban]);

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      await leadsAnalyticsService.exportCsv({
        q: q || undefined,
        status: status || undefined,
        source: source || undefined,
        sourceCampaignId: campaignFilter,
        from: fromDate || undefined,
        to: toDate || undefined,
      });
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const isFiltered = !!(q || status || source || fromDate || toDate || campaignFilter || mineOnly || needsAttention || overdueOnly || departmentFilter);
  // The board only sends status (dropped while searching), source and campaign.
  const kanbanFiltered = !!((status && !q) || source || campaignFilter);
  const kanbanEmpty = allLeads.every((l) => l.status === 'spam');

  const clearFilters = () => {
    setQ(''); setStatus(''); setSource(''); setFromDate(''); setToDate('');
    setMineOnly(false); setNeedsAttention(false); setOverdueOnly(false); setDepartmentFilter(''); setPage(1);
    if (searchParams.has('sourceCampaignId') || searchParams.has('needsAttention')) {
      const next = new URLSearchParams(searchParams);
      next.delete('sourceCampaignId');
      next.delete('needsAttention');
      setSearchParams(next, { replace: true });
    }
  };

  return (
    <>
      <Topbar
        title={t('leads')}
        breadcrumb={t('leads')}
        description={`Everyone who has reached you — from WhatsApp, ads, forms, AI chats and manual entry. New ${t('leads').toLowerCase()} land here first.`}
        right={
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" iconLeft={<Plus size={14} />} onClick={() => setCreateOpen(true)}>Add lead</Button>
            <Button
              variant="ghost"
              size="sm"
              iconLeft={<BarChart2 size={13} />}
              onClick={() => navigate('/leads/analytics')}
            >
              Analytics
            </Button>
            <Button
              variant="secondary"
              size="sm"
              iconLeft={<Download size={13} />}
              loading={exporting}
              onClick={handleExportCsv}
            >
              Export CSV
            </Button>
            <Button
              variant="secondary"
              size="sm"
              iconLeft={<Upload size={13} />}
              onClick={() => setImportOpen(true)}
            >
              Import CSV
            </Button>
          </div>
        }
      />

      <div className="px-7 py-7 w-full">
        {/* Saved views — keyed on the leads scope */}
        <SavedViewsBar
          scope="leads"
          currentFilter={{
            ...(q ? { q } : {}),
            ...(status ? { status } : {}),
            ...(source ? { source } : {}),
            ...(fromDate ? { fromDate } : {}),
            ...(toDate ? { toDate } : {}),
            ...(mineOnly ? { mineOnly: true } : {}),
            ...(needsAttention ? { needsAttention: true } : {}),
            ...(overdueOnly ? { overdueOnly: true } : {}),
          }}
          hasActiveFilter={!!(q || status || source || fromDate || toDate || mineOnly || needsAttention || overdueOnly)}
          onApply={(f) => {
            setQ(typeof f.q === 'string' ? f.q : '');
            setStatus(typeof f.status === 'string' ? f.status : '');
            setSource((typeof f.source === 'string' ? f.source : '') as '' | LeadSource);
            setFromDate(typeof f.fromDate === 'string' ? f.fromDate : '');
            setToDate(typeof f.toDate === 'string' ? f.toDate : '');
            setMineOnly(!!f.mineOnly);
            setNeedsAttention(!!f.needsAttention);
            setOverdueOnly(!!f.overdueOnly);
            setPage(1);
          }}
          onClear={() => {
            setQ(''); setStatus(''); setSource(''); setFromDate(''); setToDate('');
            setMineOnly(false); setNeedsAttention(false); setOverdueOnly(false); setPage(1);
          }}
        />

        {/* Toolbar */}
        <div className="mb-5 flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[200px]">
              <Input
                iconLeft={<Search size={15} />}
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(1); }}
                placeholder="Search by name or phone…"
                className="max-w-sm"
              />
            </div>

            {/* Date range */}
            <div className="flex flex-wrap items-center gap-1.5 text-xs text-text-muted">
              <Clock size={13} />
              <input
                type="date"
                value={fromDate}
                onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
                className="rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs text-text-primary focus:outline-none focus:border-primary"
                title="From date"
              />
              <span>–</span>
              <input
                type="date"
                value={toDate}
                onChange={(e) => { setToDate(e.target.value); setPage(1); }}
                className="rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs text-text-primary focus:outline-none focus:border-primary"
                title="To date"
              />
              {(fromDate || toDate) && (
                <button
                  onClick={() => { setFromDate(''); setToDate(''); setPage(1); }}
                  className="text-text-muted hover:text-danger text-xs"
                  title="Clear dates"
                >
                  ✕
                </button>
              )}
            </div>

            {/* Source / channel filter */}
            <Select
              value={source}
              onChange={(e) => { setSource(e.target.value as '' | LeadSource); setPage(1); }}
              className="rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs text-text-primary focus:outline-none focus:border-primary"
              title="Filter by source channel"
            >
              <option value="">All sources</option>
              <option value="ctwa">📘 Meta ad (CTWA)</option>
              <option value="lead_ad_form">📋 Meta lead form</option>
              <option value="tiktok_lead_ad">🎵 TikTok lead form</option>
              <option value="google_lead_form">🔎 Google lead form</option>
              <option value="google_ads">🔎 Google ad (CTWA)</option>
              <option value="web_form">🌐 Web form</option>
              <option value="web_chat">💬 Website chat</option>
              <option value="chat_api">🔌 App chat</option>
              <option value="email">✉️ Email</option>
              <option value="inbound_keyword">💬 WhatsApp inbound</option>
              <option value="manual">✋ Manual</option>
            </Select>

            {/* Department filter — only show when the company has departments set up. */}
            {departments.length > 0 && (
              <Select
                value={departmentFilter}
                onChange={(e) => { setDepartmentFilter(e.target.value); setPage(1); }}
                className="rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs text-text-primary focus:outline-none focus:border-primary"
                title="Filter by department"
              >
                <option value="">All departments</option>
                {departments.map((d) => (
                  <option key={d.deptUid} value={d.deptUid}>
                    {d.isDefault ? '⭐ ' : ''}{d.name}
                  </option>
                ))}
              </Select>
            )}

            {/* View mode toggle */}
            <div className="flex items-center rounded-lg border border-border bg-surface-alt p-0.5 gap-0.5">
              <button
                onClick={() => switchView('list')}
                className={clsx(
                  'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition',
                  viewMode === 'list'
                    ? 'bg-surface text-text-primary shadow-soft'
                    : 'text-text-muted hover:text-text-secondary',
                )}
                title="List view"
              >
                <LayoutList size={13} />
                List
              </button>
              <button
                onClick={() => switchView('kanban')}
                className={clsx(
                  'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition',
                  viewMode === 'kanban'
                    ? 'bg-surface text-text-primary shadow-soft'
                    : 'text-text-muted hover:text-text-secondary',
                )}
                title="Kanban view"
              >
                <Columns size={13} />
                Kanban
              </button>
            </div>
          </div>

          {/* Status tabs (list view only) */}
          {viewMode === 'list' && (
            <div className="flex gap-1.5 overflow-x-auto pb-0.5">
              {STATUS_TABS.map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => { setStatus(tab.value); setPage(1); }}
                  className={clsx(
                    'inline-flex items-center rounded-full border px-3.5 py-1.5 text-xs font-semibold whitespace-nowrap transition',
                    status === tab.value
                      ? 'border-primary bg-primary-soft text-primary'
                      : 'border-border bg-surface text-text-secondary hover:bg-surface-alt',
                  )}
                >
                  {tab.label}
                </button>
              ))}
              {campaignFilter && (
                <span className="inline-flex items-center rounded-full border border-info/20 bg-info-soft px-3.5 py-1.5 text-xs font-semibold text-info whitespace-nowrap">
                  Campaign: {campaignFilter}
                </span>
              )}
              <button
                onClick={() => { setMineOnly((v) => !v); setPage(1); }}
                className={clsx(
                  'inline-flex items-center rounded-full border px-3.5 py-1.5 text-xs font-semibold whitespace-nowrap transition',
                  mineOnly
                    ? 'border-primary bg-primary-soft text-primary'
                    : 'border-border bg-surface text-text-secondary hover:bg-surface-alt',
                )}
                title="Show only leads assigned to me"
              >
                Mine only
              </button>
              <button
                onClick={() => { setNeedsAttention((v) => !v); setPage(1); }}
                className={clsx(
                  'inline-flex items-center rounded-full border px-3.5 py-1.5 text-xs font-semibold whitespace-nowrap transition',
                  needsAttention
                    ? 'border-warning bg-warning-soft text-warning'
                    : 'border-border bg-surface text-text-secondary hover:bg-surface-alt',
                )}
                title="New leads with no bot flow attached — these need a human reply"
              >
                Needs attention
              </button>
              <button
                onClick={() => { setOverdueOnly((v) => !v); setPage(1); }}
                className={clsx(
                  'inline-flex items-center gap-1 rounded-full border px-3.5 py-1.5 text-xs font-semibold whitespace-nowrap transition',
                  overdueOnly
                    ? 'border-danger bg-danger-soft text-danger'
                    : 'border-border bg-surface text-text-secondary hover:bg-surface-alt',
                )}
                title="Leads whose follow-up date has passed"
              >
                <AlarmClock size={12} />
                Overdue
              </button>
            </div>
          )}
        </div>

        {/* ── Kanban view ──────────────────────────────────────────────────────── */}
        {viewMode === 'kanban' && (
          kanbanLoading ? (
            <div className="flex gap-4 overflow-x-auto pb-4">
              {KANBAN_COLUMNS.map((col) => (
                <div key={col.status} className="flex min-w-[220px] flex-1 flex-col gap-3">
                  <div className={clsx('skeleton h-10 rounded-xl', col.headerColor)} />
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="skeleton h-24 rounded-xl" />
                  ))}
                </div>
              ))}
            </div>
          ) : kanbanError ? (
            <LoadErrorState what={`your ${t('leads').toLowerCase()}`} onRetry={loadKanban} />
          ) : kanbanEmpty ? (
            <Card padded={false} className="overflow-hidden">
              <EmptyState filtered={kanbanFiltered} onClear={clearFilters} onAdd={() => setCreateOpen(true)} />
            </Card>
          ) : (
            <KanbanBoard leads={allLeads} setLeads={setAllLeads} />
          )
        )}

        {/* ── List view ────────────────────────────────────────────────────────── */}
        {viewMode === 'list' && (
          listError ? (
            <LoadErrorState what={`your ${t('leads').toLowerCase()}`} onRetry={loadList} />
          ) : !data ? (
            <Card padded={false} className="overflow-hidden">
              <div className="divide-y divide-border">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="flex items-center gap-4 px-5 py-4">
                    <div className="skeleton h-8 w-8 rounded-full" />
                    <div className="space-y-1.5 flex-1">
                      <div className="skeleton h-4 w-36 rounded" />
                      <div className="skeleton h-3 w-24 rounded" />
                    </div>
                    <div className="skeleton h-5 w-20 rounded-full" />
                    <div className="skeleton h-4 w-16 rounded" />
                  </div>
                ))}
              </div>
            </Card>
          ) : (
            <>
              <Card padded={false} className="overflow-hidden">
                {/* Column header */}
                <div
                  className="grid border-b border-border bg-surface-alt px-5 py-2.5"
                  style={{ gridTemplateColumns: '2fr 1fr 1.3fr 1fr 100px 80px 36px', gap: '12px' }}
                >
                  {['Lead', 'Phone', 'Source', 'Department', 'Status', 'Age', ''].map((h) => (
                    <span key={h} className="text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">
                      {h}
                    </span>
                  ))}
                </div>

                {data.items.length === 0 ? (
                  <EmptyState filtered={isFiltered} onClear={clearFilters} onAdd={() => setCreateOpen(true)} />
                ) : (
                  <div className="divide-y divide-border">
                    {data.items.map((lead) => {
                      const label = provenanceLabel(lead);
                      return (
                        <Link
                          key={lead.leadUid}
                          to={`/leads/${lead.leadUid}`}
                          className="grid items-center px-5 py-3.5 text-sm transition-colors hover:bg-surface-alt/60"
                          style={{ gridTemplateColumns: '2fr 1fr 1.3fr 1fr 100px 80px 36px', gap: '12px' }}
                        >
                          <div className="flex items-center gap-2.5 min-w-0">
                            <LeadAvatar name={lead.profileName} phone={lead.phone} />
                            <div className="min-w-0 flex items-center gap-1.5">
                              <div className="truncate font-semibold text-text-primary">
                                {lead.profileName || lead.email || 'New lead'}
                              </div>
                              {isFollowUpOverdue(lead) && (
                                <span title="Follow-up overdue" className="shrink-0 leading-none">
                                  <AlarmClock size={13} className="text-danger" />
                                </span>
                              )}
                            </div>
                          </div>
                          <span className="font-mono text-xs text-text-secondary truncate">{lead.phone || lead.email || 'No contact details'}</span>
                          <div className="min-w-0">
                            {label ? (
                              <span className="inline-flex items-center rounded-full bg-surface-alt border border-border px-2 py-0.5 text-[11px] text-text-secondary font-medium truncate max-w-full">
                                {label}
                              </span>
                            ) : (
                              <span className="text-xs text-text-muted">—</span>
                            )}
                          </div>
                          <div className="min-w-0">
                            {(() => {
                              const d = deptByUid(lead.assignedToDepartmentUid);
                              return d ? (
                                <span
                                  className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium truncate max-w-full"
                                  style={{ borderColor: `${d.color}55`, color: d.color, backgroundColor: `${d.color}15` }}
                                  title={d.description ?? ''}
                                >
                                  <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: d.color }} />
                                  {d.name}
                                </span>
                              ) : (
                                <span className="text-xs text-text-muted">—</span>
                              );
                            })()}
                          </div>
                          <LeadStatusPill status={lead.status as LeadStatus} size="sm" />
                          <span className="flex items-center gap-1 text-xs text-text-muted">
                            <Clock size={11} />
                            {relativeTime(lead.createdAt)}
                          </span>
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDeleteCandidate(lead); }}
                            title={`Delete this ${t('leads', 'singular').toLowerCase()}`}
                            className="rounded-md p-1.5 text-text-muted hover:bg-danger-soft hover:text-danger transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </Card>

              <Pagination page={data.page} total={data.total} limit={data.limit} onChange={setPage} />
            </>
          )
        )}
      </div>

      {createOpen && <LeadCreateModal onClose={() => setCreateOpen(false)} onCreated={lead => { setCreateOpen(false); navigate(`/leads/${lead.leadUid}`); }} />}
      <LeadImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => { loadList(); loadKanban(); }}
      />

      <ConfirmDialog
        open={!!deleteCandidate}
        title={`Delete this ${t('leads', 'singular').toLowerCase()}?`}
        message={
          deletingLead
            ? 'Deleting…'
            : `This will hide ${deleteCandidate?.profileName ?? deleteCandidate?.phone ?? `this ${t('leads', 'singular').toLowerCase()}`} from your ${t('leads').toLowerCase()} list. Chat history is kept for audit.`
        }
        danger
        onCancel={() => setDeleteCandidate(null)}
        onConfirm={confirmDelete}
      />
    </>
  );
}
